import api from '../../../services/axios';
import { handleApiError } from '../../../services/errorHandler';
import { prepareDateForAPI } from '../../../utils/formatDate';
import {
  IFuelsIndicatorsPetroleumDerivatives,
  ICreateFuelsIndicatorsPetroleumDerivativesDTO,
  IRefineries,
  IFilterOptions,
  IPBVariationPPIResponse,
} from './types';

export const FUELS_INDICATORS_PETROLEUM_DERIVATIVES_BASE =
  '/fuels-indicators-petroleum-derivatives';

// Tipos para navegação de datas
export interface DateInfo {
  date: string;
  totalRecords: number;
  isCurrent: boolean;
}

export interface PaginationInfo {
  hasMore: boolean;
  nextCursor?: string;
  prevCursor?: string;
}

export interface AppliedFilters {
  productLinesId?: string;
  refineriesId?: string;
}

export interface DatesNavigationResponse {
  dates: DateInfo[];
  maxDate: string;
  pagination: PaginationInfo;
  appliedFilters: AppliedFilters;
}

// Função utilitária para garantir que a data esteja correta antes de enviar para a API
const ensureProperDateFormat = (data: any) => {
  // Se não tivermos dados ou não for um objeto, retornamos os próprios dados
  if (!data || typeof data !== 'object') return data;

  // Clone os dados para não modificar o original
  const processedData = { ...data };

  // Se houver uma data de referência, usar prepareDateForAPI
  if (processedData.referenceDate) {
    try {
      processedData.referenceDate = prepareDateForAPI(processedData.referenceDate);
    } catch (err) {
      console.error('Erro ao processar data:', err);
    }
  }

  return processedData;
};

export const getFuelsIndicatorsPetroleumDerivatives = async (): Promise<
  IFuelsIndicatorsPetroleumDerivatives[]
> => {
  try {
    // Adicionar um timestamp como parâmetro para evitar cache
    const timestamp = new Date().getTime();
    const url = `${FUELS_INDICATORS_PETROLEUM_DERIVATIVES_BASE}?_t=${timestamp}`;

    const response = await api.get<IFuelsIndicatorsPetroleumDerivatives[]>(url);

    if (!response.data) {
      console.warn('Dados vazios ou inválidos retornados pela API de indicadores');
      return [];
    }

    if (!Array.isArray(response.data)) {
      console.warn('Formato de resposta inválido para indicadores:', response.data);
      return [];
    }

    return response.data;
  } catch (error) {
    console.error('Erro detalhado ao buscar indicadores de derivados de petróleo:', error);
    return [];
  }
};

export const createFuelsIndicatorsPetroleumDerivatives = async (
  data: ICreateFuelsIndicatorsPetroleumDerivativesDTO
): Promise<IFuelsIndicatorsPetroleumDerivatives> => {
  try {
    // Processar os dados para garantir que a data esteja correta
    const processedData = ensureProperDateFormat(data);

    // Verificar se temos os campos obrigatórios
    if (
      !processedData.productLinesId ||
      !processedData.refineriesId ||
      !processedData.referenceDate
    ) {
      console.error('Dados incompletos para criação:', processedData);
      throw new Error('Dados incompletos para criação do registro');
    }

    try {
      // Buscar todos os registros existentes
      const allRecords = await getFuelsIndicatorsPetroleumDerivatives();

      // Extrair data para comparação
      const recordDate = new Date(processedData.referenceDate);
      const recordDateString = `${recordDate.getFullYear()}-${String(recordDate.getMonth() + 1).padStart(2, '0')}-${String(recordDate.getDate()).padStart(2, '0')}`;

      // Buscar um registro que corresponda ao produto, refinaria e data
      const existingRecord = allRecords.find((record) => {
        // Verificar productLinesId e refineriesId
        const matchesProductAndRefinery =
          record.productLinesId === processedData.productLinesId &&
          record.refineriesId === processedData.refineriesId;

        if (!matchesProductAndRefinery) return false;

        // Verificar data (apenas dia, mês e ano)
        const existingDate = new Date(record.referenceDate);
        const existingDateString = `${existingDate.getFullYear()}-${String(existingDate.getMonth() + 1).padStart(2, '0')}-${String(existingDate.getDate()).padStart(2, '0')}`;

        return existingDateString === recordDateString;
      });

      // Se encontrar um registro existente, atualizar em vez de criar
      if (existingRecord) {
        return await updateFuelsIndicatorsPetroleumDerivatives(existingRecord.id, processedData);
      }
    } catch (checkError) {
      console.error('Erro ao verificar registros existentes:', checkError);
      // Continuar com a criação mesmo se a verificação falhar
    }

    const response = await api.post<IFuelsIndicatorsPetroleumDerivatives>(
      FUELS_INDICATORS_PETROLEUM_DERIVATIVES_BASE,
      processedData
    );

    return response.data;
  } catch (error: any) {
    console.error(`Erro detalhado ao criar indicador:`, error);

    // Detalhar informações específicas de erro
    if (error.response) {
      console.error(`Status do erro: ${error.response.status}`);
      console.error(`Dados do erro:`, error.response.data);
    }

    throw handleApiError(error);
  }
};

export const updateFuelsIndicatorsPetroleumDerivatives = async (
  id: string,
  data: Partial<ICreateFuelsIndicatorsPetroleumDerivativesDTO>
): Promise<IFuelsIndicatorsPetroleumDerivatives> => {
  try {
    // Verificar se o ID é válido
    if (!id || typeof id !== 'string' || id.trim() === '') {
      console.error('ID inválido fornecido para atualização:', id);
      throw new Error('ID inválido para atualização do registro');
    }

    // Processar os dados para garantir que a data esteja correta
    const processedData = ensureProperDateFormat(data);

    // Remover o campo id se existir, pois pode causar conflito na API
    if ('id' in processedData) {
      delete processedData.id;
    }

    // Também remover campos created_at, updated_at se existirem
    if ('created_at' in processedData) delete processedData.created_at;
    if ('updated_at' in processedData) delete processedData.updated_at;
    if ('deleted_at' in processedData) delete processedData.deleted_at;

    // Verificar se estamos atualizando com dados significativos
    const hasValidData = Object.keys(processedData).length > 0;
    if (!hasValidData) {
      console.warn('Tentativa de atualização sem dados válidos para o ID:', id);
      throw new Error('Sem dados válidos para atualização');
    }

    // Verificar campos numéricos

    // Primeiro, verificar se o registro com este ID existe
    try {
      await api.get<IFuelsIndicatorsPetroleumDerivatives>(
        `${FUELS_INDICATORS_PETROLEUM_DERIVATIVES_BASE}/${id}`
      );
    } catch (checkError) {
      console.error(`Registro com ID ${id} não encontrado para atualização:`, checkError);
      // Continuar mesmo se o registro não for encontrado, pois o backend também validará
    }

    const response = await api.patch<IFuelsIndicatorsPetroleumDerivatives>(
      `${FUELS_INDICATORS_PETROLEUM_DERIVATIVES_BASE}/${id}`,
      processedData
    );

    return response.data;
  } catch (error: any) {
    console.error(`Erro detalhado ao atualizar indicador ${id}:`, error);

    // Detalhar informações específicas de erro
    if (error.response) {
      console.error(`Status do erro: ${error.response.status}`);
      console.error(`Dados do erro:`, error.response.data);

      if (error.response.data?.message) {
        if (Array.isArray(error.response.data.message)) {
          console.error('Detalhes de validação:', error.response.data.message);
          // Mostrar cada mensagem de erro para melhor diagnóstico
          error.response.data.message.forEach((msg: string, index: number) => {
            console.error(`Erro ${index + 1}:`, msg);
          });
        } else {
          console.error('Mensagem de erro:', error.response.data.message);
        }
      }

      if (error.response.data?.errors) {
        console.error('Erros de validação específicos:', error.response.data.errors);
      }
    }

    throw handleApiError(error);
  }
};

export const deleteFuelsIndicatorsPetroleumDerivatives = async (id: string): Promise<void> => {
  try {
    await api.delete(`${FUELS_INDICATORS_PETROLEUM_DERIVATIVES_BASE}/${id}`);
  } catch (error) {
    console.error('Error deleting petroleum derivatives indicator:', error);
    throw handleApiError(error);
  }
};

export const getRefineries = async (): Promise<IRefineries[]> => {
  try {
    const response = await api.get<IRefineries[]>('/fuel-oil-refineries');

    if (!response.data) {
      console.warn('Nenhuma refinaria retornada pela API');
      return [];
    }

    if (!Array.isArray(response.data)) {
      console.warn('Formato de resposta inválido para refinarias:', response.data);
      return [];
    }

    return response.data;
  } catch (error) {
    console.error('Erro detalhado ao buscar refinarias:', error);
    return [];
  }
};

export const getFilterOptions = async (): Promise<IFilterOptions> => {
  try {
    const response = await api.get<IFilterOptions>(
      `${FUELS_INDICATORS_PETROLEUM_DERIVATIVES_BASE}/filter-options`
    );

    if (!response.data) {
      console.warn('Nenhuma opção de filtro retornada pela API');
      return {
        productLines: [],
        refineries: [],
        poleGroups: [],
      };
    }

    return response.data;
  } catch (error) {
    console.error('Erro detalhado ao buscar opções de filtro:', error);
    return {
      productLines: [],
      refineries: [],
      poleGroups: [],
    };
  }
};

/** Obtém a data mais recente disponível */
export const getMaxDateFromDatabase = async (): Promise<string> => {
  try {
    const response = await api.get(`${FUELS_INDICATORS_PETROLEUM_DERIVATIVES_BASE}/max-date`);
    return response.data.maxDate;
  } catch (error) {
    console.error('Erro ao buscar data máxima:', error);
    throw handleApiError(error);
  }
};

/** Obtém a próxima data disponível para navegação */
export const getNextAvailableDate = async (
  baseDate: string,
  direction: 'prev' | 'next',
  unit: 'day' | 'week' | 'month' = 'day',
  amount: number = 1,
  productLinesId?: string,
  refineriesId?: string
): Promise<{ nextDate: string; hasNextDate: boolean }> => {
  try {
    const params = new URLSearchParams();
    params.append('baseDate', baseDate);
    params.append('direction', direction);
    params.append('unit', unit);
    params.append('amount', amount.toString());
    if (productLinesId) params.append('productLinesId', productLinesId);
    if (refineriesId) params.append('refineriesId', refineriesId);

    const response = await api.get(
      `${FUELS_INDICATORS_PETROLEUM_DERIVATIVES_BASE}/next-available-date?${params}`
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar próxima data disponível:', error);
    throw handleApiError(error);
  }
};

// Nova função para navegação de datas usando o endpoint unificado
export const getDatesNavigation = async (params: {
  baseDate: string;
  context?: number;
  direction?: 'both' | 'prev' | 'next';
  productLinesId?: string;
  refineriesId?: string;
}): Promise<DatesNavigationResponse> => {
  try {
    const queryParams = new URLSearchParams({
      baseDate: params.baseDate,
      context: (params.context || 30).toString(),
      direction: params.direction || 'both',
    });

    if (params.productLinesId) {
      queryParams.append('productLinesId', params.productLinesId);
    }
    if (params.refineriesId) {
      queryParams.append('refineriesId', params.refineriesId);
    }

    const response = await api.get<DatesNavigationResponse>(
      `${FUELS_INDICATORS_PETROLEUM_DERIVATIVES_BASE}/dates-navigation?${queryParams.toString()}`
    );

    return response.data;
  } catch (error) {
    console.error('Erro ao buscar navegação de datas:', error);
    throw error;
  }
};

// Nova função para buscar variação PB/PPI usando o novo endpoint
export const getPBVariationPPI = async (params: {
  quantity?: number;
  baseDate: string;
  productLinesId: string;
  refineriesId?: string;
}): Promise<IPBVariationPPIResponse[]> => {
  try {
    const queryParams = new URLSearchParams({
      quantity: (params.quantity || 6).toString(),
      baseDate: params.baseDate,
      productLinesId: params.productLinesId,
    });

    if (params.refineriesId) {
      queryParams.append('refineriesId', params.refineriesId);
    }

    const response = await api.get<IPBVariationPPIResponse[]>(
      `${FUELS_INDICATORS_PETROLEUM_DERIVATIVES_BASE}/pb-variation-ppi?${queryParams.toString()}`
    );

    return response.data;
  } catch (error) {
    console.error('Erro ao buscar variação PB/PPI:', error);
    throw error;
  }
};
